import posthog from 'posthog-js';
import { SESSION_REPLAY_CONFIG } from './session-replay-config';
import { getEnvironmentInfo } from './posthog-helpers';
import { hasCookie } from './cookie-utils';

// Session-level state
let recordingStarted = false;
let recordingDecisionMade = false;

/**
 * Classify page by priority tier
 */
export function getPagePriority(pathname: string): 'high_priority' | 'landing' | 'default' {
    const { highPriorityPaths, landingPaths } = SESSION_REPLAY_CONFIG;

    // Check high priority paths (checkout, onboarding, signup)
    if (highPriorityPaths.some(path => pathname.includes(path))) {
        return 'high_priority';
    }

    // Check landing pages (homepage, marketing pages)
    if ((landingPaths as readonly string[]).includes(pathname)) {
        return 'landing';
    }

    return 'default';
}

/**
 * Calculate sampling rate for page
 */
export function getSamplingRate(pathname: string): number {
    const priority = getPagePriority(pathname);
    const { samplingRates } = SESSION_REPLAY_CONFIG;

    // High priority pages - 100%
    if (priority === 'high_priority') {
        const isCheckout = pathname.includes('checkout');
        const isOnboarding = pathname.includes('onboarding') || pathname.includes('signup');

        if (isCheckout) return samplingRates.checkout;
        if (isOnboarding) return samplingRates.onboarding;
        return samplingRates.default;
    }

    // Landing pages - 80%
    if (priority === 'landing') {
        return pathname === '/' ? samplingRates.homepage : samplingRates.landing;
    }

    // Default pages - 40%
    return samplingRates.default;
}

/**
 * Basic bot detection via user agent
 */
function isLikelyBot(): boolean {
    if (typeof navigator === 'undefined') return true;

    const botPatterns = [
        /bot/i,
        /crawl/i,
        /spider/i,
        /headless/i,
        /lighthouse/i,
        /google/i,
        /facebook/i,
    ];

    return botPatterns.some(pattern => pattern.test(navigator.userAgent));
}

/**
 * Determine if current session should be recorded
 * Uses sticky decision - once made, applies for entire session
 */
export function shouldRecordSession(pathname: string): boolean {
    // Emergency disable via environment variable
    if (process.env.NEXT_PUBLIC_DISABLE_SESSION_REPLAY === 'true') {
        console.log('[SessionReplay] Disabled via environment variable');
        return false;
    }

    // Already decided for this session
    if (recordingDecisionMade) {
        return recordingStarted;
    }

    // Environment check - production only by default
    const envInfo = getEnvironmentInfo();
    const enabledInEnv = envInfo.is_production
        ? SESSION_REPLAY_CONFIG.enableInProduction
        : envInfo.is_preview
            ? SESSION_REPLAY_CONFIG.enableInPreview
            : SESSION_REPLAY_CONFIG.enableInDevelopment;

    if (!enabledInEnv) {
        console.log('[SessionReplay] Not enabled in current environment');
        recordingDecisionMade = true;
        return false;
    }

    // Team opt-out check
    if (SESSION_REPLAY_CONFIG.respectOptOut && hasCookie('muzology_team_opt_out')) {
        console.log('[SessionReplay] Team member opted out');
        recordingDecisionMade = true;
        return false;
    }

    // Bot detection
    if (SESSION_REPLAY_CONFIG.excludeBots && isLikelyBot()) {
        console.log('[SessionReplay] Bot detected, skipping recording');
        recordingDecisionMade = true;
        return false;
    }

    // Sampling decision based on page priority
    const samplingRate = getSamplingRate(pathname);
    const shouldRecord = Math.random() < samplingRate;

    // Lock in decision for this session
    recordingDecisionMade = true;
    recordingStarted = shouldRecord;

    console.log('[SessionReplay] Decision:', {
        shouldRecord,
        pathname,
        samplingRate: `${(samplingRate * 100).toFixed(0)}%`,
        priority: getPagePriority(pathname),
    });

    return shouldRecord;
}

/**
 * Start recording with tracking event
 */
export function startConditionalRecording(pathname: string): void {
    if (typeof window === 'undefined') return;

    // Check if should record
    if (!shouldRecordSession(pathname)) {
        return;
    }

    // Start recording
    try {
        posthog.startSessionRecording();

        // Track recording started (for monitoring)
        posthog.capture('$recording_started', {
            page_path: pathname,
            page_priority: getPagePriority(pathname),
            sampling_rate: getSamplingRate(pathname),
            timestamp: Date.now(),
        });

        console.log('[SessionReplay] Recording started for:', pathname);
    } catch (error) {
        console.error('[SessionReplay] Error starting recording:', error);
    }
}

/**
 * Re-evaluate recording on page navigation
 * If already recording, continues. If not decided yet, evaluates new page.
 */
export function evaluateRecordingForPage(pathname: string): void {
    // Already recording - continue
    if (recordingStarted) {
        return;
    }

    // Already decided not to record - respect that decision
    if (recordingDecisionMade && !recordingStarted) {
        return;
    }

    // New page might trigger recording if not yet decided
    startConditionalRecording(pathname);
}

/**
 * Initialize session replay system
 * Called once on app load, waits minimum duration before starting
 */
export function initializeSessionReplay(): void {
    if (typeof window === 'undefined') return;

    console.log('[SessionReplay] Initializing...');

    // Wait minimum duration before recording (filter bounces)
    setTimeout(() => {
        startConditionalRecording(window.location.pathname);
    }, SESSION_REPLAY_CONFIG.minimumDuration);
}
